import { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from './store/auth';
import { apiBaseUrl } from './lib/api';
import type { AuthTokens } from '@marnic/shared';
import { Layout } from './components/Layout';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { Calls } from './pages/Calls';
import { Callbacks } from './pages/Callbacks';
import { Leads } from './pages/Leads';
import { LeadDetail } from './pages/LeadDetail';
import { Campaigns } from './pages/Campaigns';
import { CampaignDetail } from './pages/CampaignDetail';
import { Supervisor } from './pages/Supervisor';
import { Compliance } from './pages/Compliance';
import { Settings } from './pages/Settings';
import { Reports } from './pages/Reports';
import { CallLauncher } from './pages/CallLauncher';
import { WhatsApp } from './pages/WhatsApp';
import { Sms } from './pages/Sms';

export function App(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const hasHydrated = useAuthStore((s) => s.hasHydrated);
  const setSession = useAuthStore((s) => s.setSession);
  const location = useLocation();

  // If the URL hash contains #ptoken=<jwt>, the user arrived via Perfex
  // click-to-call (dial_lead mints a JWT and redirects with it in the fragment).
  // Exchange it for a dialler session before the normal auth routing runs.
  const [ssoProcessed, setSsoProcessed] = useState(
    () => !window.location.hash.includes('ptoken='),
  );

  useEffect(() => {
    if (ssoProcessed) return;
    const match = /[#&]ptoken=([^&]+)/.exec(window.location.hash);
    if (!match) {
      setSsoProcessed(true);
      return;
    }
    // Clear the token from the address bar immediately so it isn't in history.
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
    fetch(`${apiBaseUrl}/auth/perfex-sso`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: decodeURIComponent(match[1]!) }),
    })
      .then(async (res) => {
        if (res.ok) setSession((await res.json()) as AuthTokens);
      })
      .catch(() => {})
      .finally(() => setSsoProcessed(true));
  }, [ssoProcessed, setSession]);

  if (!hasHydrated || !ssoProcessed) {
    return <div className="min-h-screen bg-ink-50" />;
  }

  if (!user) {
    const from = `${location.pathname}${location.search}`;
    if (from !== '/login') {
      sessionStorage.setItem('marnic.postLoginRedirect', from);
    }
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace state={{ from }} />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<Layout />}>
        <Route index element={<Dashboard />} />
        <Route path="call" element={<CallLauncher />} />
        <Route path="calls" element={<Calls />} />
        <Route
          path="reports/*"
          element={user.role === 'admin' ? <Reports /> : <Navigate to="/" replace />}
        />
        <Route path="callbacks" element={<Callbacks />} />
        <Route path="whatsapp" element={<WhatsApp />} />
        <Route path="sms" element={<Sms />} />
        <Route path="leads" element={<Leads />} />
        <Route path="leads/:id" element={<LeadDetail />} />
        <Route path="campaigns" element={<Campaigns />} />
        <Route path="campaigns/:id" element={<CampaignDetail />} />
        <Route
          path="supervisor"
          element={user.role === 'agent' ? <Navigate to="/" replace /> : <Supervisor />}
        />
        <Route
          path="compliance"
          element={user.role === 'agent' ? <Navigate to="/" replace /> : <Compliance />}
        />
        <Route path="settings" element={<Settings />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
      <Route path="/login" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
